import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toastService } from '../services/toastService';
import type { CommunicationChannel } from '../types';
import { channelsService } from '../services/channelsService';
import { zernioService } from '../services/zernioService';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { Logger } from '../utils/logger';
// V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — kill `error: any` via helper unifié
import { extractApiErrorMessage } from '../utils/apiError';

export function useChannels() {
  const { t } = useLanguage();
  const [communicationChannels, setCommunicationChannels] = useState<CommunicationChannel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentSpace } = useAuth();

  // Load channels from API when space changes
  const loadChannels = useCallback(async () => {
    if (!currentSpace?.id) {
      setCommunicationChannels([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      Logger.debug('[useChannels] Loading channels', { spaceId: currentSpace.id });
      const channels = await channelsService.getAll(currentSpace.id);
      setCommunicationChannels(channels);
      Logger.info('[useChannels] Channels loaded', {
        spaceId: currentSpace.id,
        count: channels.length,
      });

      // Sync Zernio accounts with Raiso channels silently
      try {
        const syncResult = await zernioService.syncAccounts();
        if (syncResult.synced.length > 0) {
          Logger.info('[useChannels] Zernio accounts synced', {
            spaceId: currentSpace.id,
            syncedCount: syncResult.synced.length,
          });
          // Reload channels to reflect sync changes.
          // V4-API-DOUBLE-FETCH-CHANNELS — bypass le cache 30s, le sync vient
          // de muter l'état serveur côté channels.
          channelsService.invalidateCache(currentSpace.id);
          const refreshed = await channelsService.getAll(currentSpace.id);
          setCommunicationChannels(refreshed);
        }
      } catch (syncErr) {
        Logger.warn('[useChannels] Zernio sync failed (non-blocking)', {
          spaceId: currentSpace.id,
          error: syncErr instanceof Error ? syncErr.message : 'unknown_error',
        });
      }
    } catch (err) {
      Logger.error('[useChannels] Failed to load channels', {
        spaceId: currentSpace.id,
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      setError(t.toasts.channelLoadError);
      // Do NOT reset to [] — keep existing channels so the UI stays intact on transient failures
    } finally {
      setIsLoading(false);
    }
    // `t` est requis depuis que le message d'erreur est i18n : sans lui, un changement
    // de langue laisserait la closure sur l'ancienne traduction. `t` est stable par
    // langue (objet `translations[language]`) et `channelsService.getAll` est caché 30 s,
    // donc le re-run au changement de langue ne coûte pas d'appel réseau supplémentaire.
  }, [currentSpace?.id, t]);

  const getChannelDisplayName = (channel: CommunicationChannel): string => {
    return channel.customName || channel.profileName || channel.network || 'Canal';
  };

  // Load channels on mount and when space changes
  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Listen for Zernio connection changes (connect/disconnect via popup)
  // This is more reliable than prop drilling onRefreshChannels through 7 component layers
  useEffect(() => {
    const handleLateConnectionChanged = () => {
      Logger.info('[useChannels] Zernio connection changed, reloading channels');
      // V4-API-DOUBLE-FETCH-CHANNELS — l'event vient explicitement signaler
      // une mutation des channels (connect/disconnect Zernio), donc on doit
      // dépasser le cache.
      if (currentSpace?.id) channelsService.invalidateCache(currentSpace.id);
      loadChannels();
    };
    window.addEventListener('late-connection-changed', handleLateConnectionChanged);
    return () => window.removeEventListener('late-connection-changed', handleLateConnectionChanged);
  }, [loadChannels]);

  const addCommunicationChannel = async (channelData: Omit<CommunicationChannel, 'id'>) => {
    if (!currentSpace) {
      toastService.error(t.toasts?.noSpaceSelected || 'Erreur: Aucun espace sélectionné');
      return;
    }
    try {
      const data = await channelsService.create(currentSpace.id, channelData);
      
      if (data) {
        setCommunicationChannels((prev) => [...prev, data]);
        toastService.success(
          t.toasts.channelAddedTitle,
          t.toasts.channelAddedDesc.replace('{name}', getChannelDisplayName(data))
        );
        // V4-CHANNELS-NO-WARN-POSITIONING 2026-05-29 — FINDING-CHANNELS-03 :
        // le positionnement spécifique est OPTIONNEL — afficher un toast warning
        // à chaque création était bruyant et culpabilisant. Le hint reste affiché
        // de façon passive dans la card du canal (`noPositioning` chip dans
        // ChannelListItem) pour qui veut affiner. Pas de toast.
        Logger.info('[useChannels] Channel created', {
          channelId: data.id,
          network: data.network,
          spaceId: currentSpace.id,
        });
      }
    } catch (error: unknown) {
      const errMessage = error instanceof Error ? error.message : 'unknown_error';
      Logger.error('[useChannels] Failed to create channel', {
        spaceId: currentSpace.id,
        error: errMessage,
      });
      // FastAPI peut retourner `detail` soit en string, soit en array d'erreurs
      // de validation Pydantic `[{msg, loc, type, ...}]`. On gère les deux cas
      // sans recourir à `any`.
      let message: string = t.toasts.channelAddError;
      if (axios.isAxiosError(error)) {
        const rawDetail = error.response?.data?.detail;
        if (Array.isArray(rawDetail)) {
          message = rawDetail
            .map((e: unknown) => {
              if (e && typeof e === 'object' && 'msg' in e && typeof (e as { msg: unknown }).msg === 'string') {
                return (e as { msg: string }).msg;
              }
              return JSON.stringify(e);
            })
            .join(', ');
        } else if (typeof rawDetail === 'string' && rawDetail) {
          message = rawDetail;
        } else {
          message = extractApiErrorMessage(error, t.toasts.channelAddError);
        }
      } else {
        message = extractApiErrorMessage(error, t.toasts.channelAddError);
      }
      toastService.error(message);
    }
  };

  const updateCommunicationChannel = async (id: string, channelData: Omit<CommunicationChannel, 'id'>) => {
    try {
      const data = await channelsService.update(id, channelData);
      
      if (data) {
        setCommunicationChannels((prev) =>
          prev.map((channel) => (channel.id === id ? data : channel))
        );
        toastService.success(
          t.toasts.channelUpdatedTitle,
          t.toasts.channelUpdatedDesc.replace('{name}', getChannelDisplayName(data))
        );
        // V4-CHANNELS-NO-WARN-POSITIONING 2026-05-29 — FINDING-CHANNELS-03 :
        // pas de toast warning si pas de positioning spécifique (champ optionnel).
        Logger.info('[useChannels] Channel updated', {
          channelId: id,
          network: data.network,
        });
      }
    } catch (error: unknown) {
      const errMessage = error instanceof Error ? error.message : 'unknown_error';
      Logger.error('[useChannels] Failed to update channel', {
        channelId: id,
        error: errMessage,
      });
      toastService.error(extractApiErrorMessage(error, t.toasts.channelUpdateError));
    }
  };

  const deleteCommunicationChannel = async (id: string) => {
    try {
      await channelsService.delete(id);
      setCommunicationChannels((prev) => prev.filter((channel) => channel.id !== id));
      toastService.success(t.toasts.channelDeletedTitle, t.toasts.channelDeletedDesc);
      Logger.warn('[useChannels] Channel deleted', { channelId: id });
    } catch (error: unknown) {
      const errMessage = error instanceof Error ? error.message : 'unknown_error';
      Logger.error('[useChannels] Failed to delete channel', {
        channelId: id,
        error: errMessage,
      });
      const message = extractApiErrorMessage(error, t.toasts.channelDeleteError);
      toastService.error(message);
      setError(message);
    }
  };

  return {
    communicationChannels,
    setCommunicationChannels,
    addCommunicationChannel,
    updateCommunicationChannel,
    deleteCommunicationChannel,
    loadChannels,
    isLoading,
    error
  };
}

